'use client';

/**
 * Admin Dashboard Layout
 * Provides sidebar navigation, header, and main content area
 *
 * Navigation Structure:
 * - Global Admins (Penny Admins): See Platform section + Organisation section
 * - Org Admins: See only Organisation section for their org
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/auth/AuthContext';
import { signOutAdminAndRedirect } from '@/lib/auth/adminLogout';
import { supabase } from '@/lib/supabase/client';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AgentInactivityGuard } from '@/components/auth/AgentInactivityGuard';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Workflow, Users, Building2, Settings, Menu, X, ChevronDown, ChevronLeft, ChevronRight, LogOut, Shield, UserCog, BarChart3, PanelLeftClose, PanelLeftOpen, Newspaper, Coins, ShieldCheck, Star } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { OrganizationWithRole } from '@/types/organization';
import { getContrastForeground, tintForDarkMode, tintForLightMode } from '@/lib/ui/colour-utils';
import { useFavicon } from '@/lib/ui/useFavicon';
import { isBrandingPreviewPath } from '@/lib/ui/brandingPreview';
import { OrgSelectorModal } from '@/components/org/OrgSelectorModal';
import { MFAEnrollmentWizard } from '@/components/auth/MFAEnrollmentWizard';
import { usePermission } from '@/hooks/usePermission';
import { ReleaseVersion } from '@/components/release/ReleaseVersion';

// Helper to build hierarchical organization list for the org switcher
interface OrgWithDepth extends OrganizationWithRole {
  depth: number;
}
function buildOrgHierarchy(orgs: OrganizationWithRole[]): OrgWithDepth[] {
  const result: OrgWithDepth[] = [];
  const orgMap = new Map(orgs.map(org => [org.id, org]));

  // Find root orgs (no parent or parent not in list)
  const rootOrgs = orgs.filter(org => !org.parent_org_id || !orgMap.has(org.parent_org_id));

  // Recursively add orgs with their children
  const addOrgWithChildren = (org: OrganizationWithRole, depth: number) => {
    result.push({
      ...org,
      depth
    });
    // Find children of this org
    const children = orgs.filter(child => child.parent_org_id === org.id);
    // Sort children alphabetically
    children.sort((a, b) => a.name.localeCompare(b.name));
    children.forEach(child => addOrgWithChildren(child, depth + 1));
  };

  // Sort root orgs alphabetically
  rootOrgs.sort((a, b) => a.name.localeCompare(b.name));
  rootOrgs.forEach(org => addOrgWithChildren(org, 0));
  return result;
}
interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  /** Only show for Org Admins (not Penny Admins) */
  orgAdminOnly?: boolean;
  section: 'overview' | 'operations' | 'build' | 'administration' | 'platform';
  description?: string;
  /** Child navigation items for expandable parent menus */
  children?: NavItem[];
  /**
   * RBAC v2 permission gate. When set, the item is rendered via the
   * `<PermissionGatedNav />` subcomponent which probes the engine and shows
   * the entry only when the caller holds `slug` for the current organisation.
   */
  permission?: {
    slug: string;
  };
  /**
   * Restrict the item to one side of the org hierarchy. `'parent'` items only
   * appear when the selected org is a root parent; `'child'` only when it is
   * a child. The matching `<ProtectedRoute requiredOrgScope>` enforces the
   * same rule for direct-URL access. Items without `orgScope` show in either
   * context. Penny Admins are not exempt — the rule is contextual to the
   * currently switched-into org.
   */
  orgScope?: 'parent' | 'child';
}

// Navigation items grouped by section. Sections mirror the permission
// catalogue's natural clustering (operations / build / administration /
// platform) so users only see groups whose permissions they actually hold.
// Phase 5h.3b restructured the nav around the entity catalogue and fixed
// three broken slugs (`agents.manage`, `users.manage_global_admins`,
// `cms.manage`) that no longer exist in the seed.
const allNavItems: NavItem[] = [
// Overview — visible to everyone with any access at all.
{
  name: 'Dashboard',
  href: '/admin',
  icon: LayoutDashboard,
  section: 'overview',
  description: 'Overview and stats'
},
// Operations — per-org day-to-day surfaces.
{
  name: 'Customers',
  href: '/admin/customers',
  icon: Users,
  permission: {
    slug: 'customers.view'
  },
  orgScope: 'child',
  section: 'operations',
  description: 'View customer sessions'
},
// The slug here is a placeholder — it is always overridden per render with
// the topology-aware `reports.view_child` / `reports.view_parent` slug (see
// `reportingNavSlug` below). Kept non-empty so the item stays permission-gated.
{
  name: 'Reporting',
  href: '/admin/reporting',
  icon: BarChart3,
  permission: {
    slug: 'reports.view_parent'
  },
  section: 'operations',
  description: 'View embedded reporting dashboards'
},
// Child orgs only: feedback is collected per (workflow, child org) and there
// is no parent-level roll-up, so `feedback.view_parent` does not exist yet.
{
  name: 'Feedback',
  href: '/admin/feedback',
  icon: Star,
  permission: {
    slug: 'feedback.view_child'
  },
  orgScope: 'child',
  section: 'operations',
  description: 'Anonymous customer ratings and comments'
}, {
  name: 'Usage',
  href: '/admin/usage',
  icon: Coins,
  permission: {
    slug: 'usage.view'
  },
  section: 'operations',
  description: 'View usage and billing metrics'
},
// Build — per-org authoring surfaces.
{
  name: 'Workflows',
  href: '/admin/workflows',
  icon: Workflow,
  permission: {
    slug: 'workflows.manage_templates'
  },
  section: 'build',
  description: 'Build and manage workflows'
},
// Administration — per-org admin surfaces. Team and Roles & Permissions
// are managed at the parent-org level only; child orgs inherit them.
{
  name: 'Team',
  href: '/admin/users',
  icon: UserCog,
  permission: {
    slug: 'team.view'
  },
  orgScope: 'parent',
  section: 'administration',
  description: 'Manage team members'
}, {
  name: 'Roles & Permissions',
  href: '/admin/roles-permissions',
  icon: ShieldCheck,
  permission: {
    slug: 'rbac.view'
  },
  orgScope: 'parent',
  section: 'administration',
  description: 'Manage RBAC v2 roles, permissions and assignments'
},
// Platform — global-admin-only platform-wide surfaces.
// `organisations.manage` (not `.view`) matches the page-level
// `ProtectedRoute` guard on `/admin/organizations`. `.view` is the RLS
// read-path slug granted to org admin/owner/report for in-org data access
// and would surface this menu entry to roles that get bounced at the page.
{
  name: 'Organisations',
  href: '/admin/organizations',
  icon: Building2,
  permission: {
    slug: 'organisations.manage'
  },
  section: 'platform',
  description: 'Manage all organisations'
}, {
  name: 'Global Admins',
  href: '/admin/global-admins',
  icon: Shield,
  permission: {
    slug: 'global_admins.manage'
  },
  section: 'platform',
  description: 'Manage global administrators'
}, {
  name: 'Landing Page',
  href: '/admin/cms',
  icon: Newspaper,
  permission: {
    slug: 'landing_pages.edit'
  },
  section: 'platform',
  description: 'Manage landing page content'
}, {
  name: 'Billing',
  href: '/admin/billing',
  icon: Coins,
  permission: {
    slug: 'billing.manage_platform'
  },
  section: 'platform',
  description: 'Platform-wide billing and usage'
}, {
  name: 'Global Settings',
  href: '/admin/global-settings',
  icon: Settings,
  permission: {
    slug: 'global_settings.view'
  },
  section: 'platform',
  description: 'Platform-wide configuration'
}];

// Flattened list of every href in the nav (parents + children), used by
// `isActiveRoute` to defer prefix matches to the most specific entry. Computed
// once at module load — `allNavItems` never changes at runtime.
const ALL_NAV_HREFS: readonly string[] = (() => {
  const out: string[] = [];
  const collect = (items: NavItem[]) => {
    for (const it of items) {
      out.push(it.href);
      if (it.children) collect(it.children);
    }
  };
  collect(allNavItems);
  return out;
})();

/**
 * Wrapper that calls `usePermission` per nav item with a `permission` gate.
 * Defined as a top-level subcomponent so the hook is invoked unconditionally
 * on every render — looping `usePermission` inside `navItems.map(...)` of
 * the parent would violate the rules of hooks.
 *
 * Visibility rules:
 *   - currentOrgId is null → org-less carve-out: show iff
 *     `isPlatformAdmin` (preserves today's behaviour for org-less
 *     global-admin sessions; the flag is the RBAC v2 canonical
 *     replacement for the legacy `user_type === 'global_admin'` check). The hook
 *     is still called with a null orgId so it short-circuits cleanly.
 *   - probe in flight → render nothing (avoids the nav re-shuffling after
 *     each item resolves at a different time).
 *   - otherwise → show iff the engine returns `allowed`.
 *
 * Optional `onVisibilityChange` lets the parent <NavSection> aggregate
 * per-item visibility so the section header is hidden when every gated
 * item resolves to false — closes the long-standing bug where section
 * labels remained on screen with no items underneath.
 */
export function PermissionGatedNav(t0) {
  const $ = _c(18);
  const {
    item,
    currentOrgId,
    isPlatformAdmin,
    render,
    onVisibilityChange
  } = t0;
  const slug = item.permission?.slug ?? null;
  const {
    allowed,
    loading
  } = usePermission(slug, currentOrgId ?? null);
  let visible;
  if ($[0] !== allowed || $[1] !== currentOrgId || $[2] !== isPlatformAdmin || $[3] !== loading) {
    if (!currentOrgId) {
      visible = isPlatformAdmin;
    } else {
      if (loading) {
        visible = false;
      } else {
        visible = allowed;
      }
    }
    $[0] = allowed;
    $[1] = currentOrgId;
    $[2] = isPlatformAdmin;
    $[3] = loading;
    $[4] = visible;
  } else {
    visible = $[4];
  }
  const onVisibilityChangeRef = React.useRef(onVisibilityChange);
  let t1;
  let t2;
  if ($[5] !== onVisibilityChange) {
    t1 = () => {
      onVisibilityChangeRef.current = onVisibilityChange;
    };
    t2 = [onVisibilityChange];
    $[5] = onVisibilityChange;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[8] !== item.href || $[9] !== visible) {
    t3 = () => {
      onVisibilityChangeRef.current?.(item.href, visible);
      return () => {
        onVisibilityChangeRef.current?.(item.href, false);
      };
    };
    $[8] = item.href;
    $[9] = visible;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== item.href || $[12] !== visible) {
    t4 = [item.href, visible];
    $[11] = item.href;
    $[12] = visible;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  React.useEffect(t3, t4);
  let t5;
  if ($[14] !== item || $[15] !== render || $[16] !== visible) {
    t5 = visible ? render(item) : null;
    $[14] = item;
    $[15] = render;
    $[16] = visible;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}

/**
 * Renders one labelled nav section and hides its header when every item is
 * either unrendered (permission-denied) or filtered out. Ungated items are
 * always counted as visible — their presence alone keeps the section header
 * on screen.
 */
export function NavSection(t0) {
  const $ = _c(21);
  const {
    items,
    label,
    options,
    currentOrgId,
    isPlatformAdmin,
    renderItem,
    renderLabel
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [visibleGated, setVisibleGated] = React.useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (href, visible) => {
      setVisibleGated(prev => {
        const isIn = prev.has(href);
        if (visible && !isIn) {
          const next = new Set(prev);
          next.add(href);
          return next;
        }
        if (!visible && isIn) {
          const next_0 = new Set(prev);
          next_0.delete(href);
          return next_0;
        }
        return prev;
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onVisibilityChange = t2;
  if (items.length === 0) {
    return null;
  }
  let t3;
  if ($[2] !== items) {
    t3 = items.some(_temp);
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hasUngated = t3;
  const sectionVisible = hasUngated || visibleGated.size > 0;
  let t4;
  if ($[4] !== label || $[5] !== options || $[6] !== renderLabel || $[7] !== sectionVisible) {
    t4 = label && sectionVisible && !options.collapsed && renderLabel(label);
    $[4] = label;
    $[5] = options;
    $[6] = renderLabel;
    $[7] = sectionVisible;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== currentOrgId || $[10] !== isPlatformAdmin || $[11] !== items || $[12] !== renderItem) {
    let t6;
    if ($[14] !== currentOrgId || $[15] !== isPlatformAdmin || $[16] !== renderItem) {
      t6 = item_0 => {
        if (item_0.permission) {
          return <PermissionGatedNav key={item_0.href} item={item_0} currentOrgId={currentOrgId} isPlatformAdmin={isPlatformAdmin} render={renderItem} onVisibilityChange={onVisibilityChange} />;
        }
        return <React.Fragment key={item_0.href}>{renderItem(item_0)}</React.Fragment>;
      };
      $[14] = currentOrgId;
      $[15] = isPlatformAdmin;
      $[16] = renderItem;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    t5 = items.map(t6);
    $[9] = currentOrgId;
    $[10] = isPlatformAdmin;
    $[11] = items;
    $[12] = renderItem;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div className="space-y-px">{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
}

/**
 * Returns the path to redirect to after an organisation switch, or null when
 * no redirect is required. We only redirect when the selected org actually
 * changed (re-selecting the current org is a no-op) and when the next org is
 * non-null (a sign-out style clear must not bounce the user to `/admin`).
 *
 * Exported for unit tests; consumed by `AdminLayoutContent.handleOrgSwitch`.
 */
function _temp(item) {
  return !item.permission;
}
export function pickOrgSwitchDestination(previousOrgId: string | null, nextOrgId: string | null): string | null {
  if (!nextOrgId) return null;
  if (nextOrgId === previousOrgId) return null;
  return '/admin';
}
export function resolveBrandAccent(primaryColor: string, isDark: boolean): string {
  return isDark ? tintForDarkMode(primaryColor) : tintForLightMode(primaryColor);
}
function AdminLayoutContent(t0) {
  const $ = _c(179);
  const {
    children
  } = t0;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoErrorOrgId, setLogoErrorOrgId] = useState(null);
  const [avatarLogoErrorOrgId, setAvatarLogoErrorOrgId] = useState(null);
  const [orgSelectorOpen, setOrgSelectorOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const pathname = usePathname();
  const router = useRouter();
  const {
    user,
    userProfile,
    currentOrganization,
    organizations,
    setCurrentOrganization,
    signOut,
    isMFAEnrolled,
    userHasMfaBypass,
    isPlatformAdmin,
    loading: authLoading
  } = useAuth();
  useFavicon(currentOrganization?.favicon_url);
  let t1;
  if ($[0] !== currentOrganization?.id || $[1] !== router || $[2] !== setCurrentOrganization) {
    t1 = org => {
      setCurrentOrganization(org);
      const destination = pickOrgSwitchDestination(currentOrganization?.id ?? null, org?.id ?? null);
      if (destination) {
        router.push(destination);
      }
    };
    $[0] = currentOrganization?.id;
    $[1] = router;
    $[2] = setCurrentOrganization;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleOrgSwitch = t1;
  let t2;
  if ($[4] !== signOut) {
    t2 = async () => {
      await signOutAdminAndRedirect({
        supabase,
        signOut
      });
    };
    $[4] = signOut;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleSignOut = t2;
  const [showMFAWizard, setShowMFAWizard] = useState(false);
  const [, setMfaCheckDone] = useState(false);
  let t3;
  if ($[6] !== authLoading || $[7] !== currentOrganization?.id || $[8] !== currentOrganization?.settings?.features?.enforce_mfa || $[9] !== isMFAEnrolled || $[10] !== isPlatformAdmin || $[11] !== user || $[12] !== userHasMfaBypass) {
    t3 = () => {
      if (!user || authLoading) {
        return;
      }
      const shouldEnforceMFA = isPlatformAdmin || currentOrganization?.settings?.features?.enforce_mfa === true;
      if (!shouldEnforceMFA) {
        setMfaCheckDone(true);
        return;
      }
      let cancelled = false;
      Promise.all([isMFAEnrolled(), userHasMfaBypass(currentOrganization?.id ?? null)]).then(t4 => {
        const [enrolled, bypass] = t4;
        if (cancelled) {
          return;
        }
        setShowMFAWizard(!enrolled && !bypass);
        setMfaCheckDone(true);
      });
      return () => {
        cancelled = true;
      };
    };
    $[6] = authLoading;
    $[7] = currentOrganization?.id;
    $[8] = currentOrganization?.settings?.features?.enforce_mfa;
    $[9] = isMFAEnrolled;
    $[10] = isPlatformAdmin;
    $[11] = user;
    $[12] = userHasMfaBypass;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== authLoading || $[15] !== currentOrganization || $[16] !== isMFAEnrolled || $[17] !== isPlatformAdmin || $[18] !== user || $[19] !== userHasMfaBypass) {
    t4 = [user, authLoading, isPlatformAdmin, currentOrganization, isMFAEnrolled, userHasMfaBypass];
    $[14] = authLoading;
    $[15] = currentOrganization;
    $[16] = isMFAEnrolled;
    $[17] = isPlatformAdmin;
    $[18] = user;
    $[19] = userHasMfaBypass;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  useEffect(t3, t4);
  let t5;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowMFAWizard(false);
    };
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handleMFAComplete = t5;
  let t6;
  if ($[22] !== pathname) {
    let t7;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = /^\/admin\/workflows\/[^/]+$/;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    t6 = pathname.match(t7);
    $[22] = pathname;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const isFullScreenEditor = t6;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  let t7;
  let t8;
  if ($[25] !== isFullScreenEditor) {
    t7 = () => {
      if (isFullScreenEditor) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };
    t8 = [isFullScreenEditor];
    $[25] = isFullScreenEditor;
    $[26] = t7;
    $[27] = t8;
  } else {
    t7 = $[26];
    t8 = $[27];
  }
  useEffect(t7, t8);
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setActiveSubMenu(null);
    };
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== pathname) {
    t10 = [pathname];
    $[29] = pathname;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  useEffect(t9, t10);
  const logoError = logoErrorOrgId === currentOrganization?.id;
  const avatarLogoError = avatarLogoErrorOrgId === currentOrganization?.id;
  const isGlobalAdmin = isPlatformAdmin;
  let t11;
  if ($[31] !== pathname) {
    t11 = href => {
      if (pathname === href) {
        return true;
      }
      if (href === "/admin") {
        return false;
      }
      if (!pathname.startsWith(href + "/")) {
        return false;
      }
      return !ALL_NAV_HREFS.some(other => other !== href && other.length > href.length && other.startsWith(href + "/") && (pathname === other || pathname.startsWith(other + "/")));
    };
    $[31] = pathname;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const isActiveRoute = t11;
  let t12;
  if ($[33] !== isActiveRoute) {
    t12 = (item, options) => {
      const isActive = isActiveRoute(item.href);
      const isCollapsed = options.collapsed ?? false;
      const isSubMenu = options.isSubMenu ?? false;
      return <Link key={item.href} href={item.href} title={isCollapsed ? item.name : undefined} onClick={options.onClick} className={`group relative flex items-center rounded-lg border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background ${isCollapsed ? "justify-center p-2" : isSubMenu ? "gap-2 px-2.5 py-1.5 text-[13px]" : "gap-2.5 px-2.5 py-[7px] text-[13px]"} ${isActive ? "glass-subtle text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.08] dark:hover:bg-white/[0.04]"}`}><item.icon className={`flex-shrink-0 ${isSubMenu ? "h-3.5 w-3.5" : "h-4 w-4"}`} />{!isCollapsed && <span className="truncate">{item.name}</span>}</Link>;
    };
    $[33] = isActiveRoute;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const renderNavLink = t12;
  let t13;
  if ($[35] !== isActiveRoute || $[36] !== renderNavLink) {
    t13 = (item_0, options_0) => {
      if (!item_0.children || item_0.children.length === 0) {
        return renderNavLink(item_0, options_0);
      }
      const isCollapsed_0 = options_0.collapsed ?? false;
      const isAnyChildActive = item_0.children.some(child => isActiveRoute(child.href));
      if (isCollapsed_0) {
        return renderNavLink(item_0, options_0);
      }
      return <button key={item_0.name} onClick={() => setActiveSubMenu(item_0)} className={`group flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-[7px] text-[13px] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-1 focus-visible:ring-offset-background ${isAnyChildActive ? "glass-subtle text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.08] dark:hover:bg-white/[0.04]"}`}><item_0.icon className="h-4 w-4 flex-shrink-0" /><span className="truncate">{item_0.name}</span><ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/60" /></button>;
    };
    $[35] = isActiveRoute;
    $[36] = renderNavLink;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const renderNavItem = t13;
  const renderSectionLabel = _temp2;
  let t14;
  if ($[38] !== renderNavLink) {
    t14 = (parentItem, options_1) => <div className="flex flex-col h-full"><button onClick={() => setActiveSubMenu(null)} className="group flex items-center gap-2 px-2.5 py-2 text-[13px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 rounded-lg mx-0.5 mb-1 hover:bg-white/[0.08] dark:hover:bg-white/[0.04]"><ChevronLeft className="h-3.5 w-3.5" /><span>Back</span></button><div className="px-3 pb-2 mb-1 border-b border-border/20"><div className="flex items-center gap-2"><parentItem.icon className="h-4 w-4 text-foreground" /><span className="text-[13px] font-semibold text-foreground">{parentItem.name}</span></div>{parentItem.description && <p className="text-[11px] text-muted-foreground/70 mt-0.5 ml-6">{parentItem.description}</p>}</div><div className="space-y-px px-0.5">{parentItem.children?.map(child_0 => renderNavLink(child_0, {
          ...options_1,
          isSubMenu: false
        }))}</div></div>;
    $[38] = renderNavLink;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const renderSubMenuPage = t14;
  const isChildOrgContext = !!currentOrganization?.parent_org_id;
  const isParentOrgContext = !!currentOrganization && !isChildOrgContext;
  const reportingNavSlug = isChildOrgContext ? "reports.view_child" : "reports.view_parent";
  let t15;
  if ($[40] !== reportingNavSlug) {
    t15 = item_1 => item_1.href === "/admin/reporting" ? {
      ...item_1,
      permission: {
        slug: reportingNavSlug
      }
    } : item_1;
    $[40] = reportingNavSlug;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== currentOrganization || $[43] !== isChildOrgContext || $[44] !== isGlobalAdmin || $[45] !== isParentOrgContext) {
    t16 = item_2 => {
      if (item_2.orgAdminOnly && isGlobalAdmin) {
        return false;
      }
      if (item_2.orgScope && currentOrganization) {
        if (item_2.orgScope === "parent" && !isParentOrgContext) {
          return false;
        }
        if (item_2.orgScope === "child" && !isChildOrgContext) {
          return false;
        }
      }
      if (item_2.href === "/admin/reporting") {
        if (!isGlobalAdmin) {
          const reporting = currentOrganization?.settings?.features?.reporting;
          if (!reporting?.enabled) {
            return false;
          }
        }
      }
      return true;
    };
    $[42] = currentOrganization;
    $[43] = isChildOrgContext;
    $[44] = isGlobalAdmin;
    $[45] = isParentOrgContext;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const filteredNavItems = allNavItems.map(t15).filter(t16);
  const navSections = {
    overview: filteredNavItems.filter(_temp3),
    operations: filteredNavItems.filter(_temp4),
    build: filteredNavItems.filter(_temp5),
    administration: filteredNavItems.filter(_temp6),
    platform: filteredNavItems.filter(_temp7)
  };
  const hexToRgb = _temp9;
  const orgPrimary = currentOrganization?.primary_color || "#6366f1";
  const orgSecondary = currentOrganization?.secondary_color || "#818cf8";
  const orgPrimaryRgb = hexToRgb(orgPrimary) || "99 102 241";
  const orgSecondaryRgb = hexToRgb(orgSecondary) || "129 140 248";
  let t17;
  if ($[47] !== orgPrimary || $[48] !== orgPrimaryRgb || $[49] !== orgSecondary || $[50] !== orgSecondaryRgb) {
    t17 = {
      "--org-primary": orgPrimary,
      "--org-secondary": orgSecondary,
      "--org-primary-rgb": orgPrimaryRgb,
      "--org-secondary-rgb": orgSecondaryRgb
    };
    $[47] = orgPrimary;
    $[48] = orgPrimaryRgb;
    $[49] = orgSecondary;
    $[50] = orgSecondaryRgb;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  const cssVariables = t17 as React.CSSProperties;
  let t18;
  let t19;
  if ($[52] !== orgPrimary || $[53] !== orgPrimaryRgb || $[54] !== orgSecondary || $[55] !== orgSecondaryRgb) {
    t18 = () => {
      const root = document.documentElement;
      const primaryColor = orgPrimary;
      const secondaryColor = orgSecondary;
      const updateThemeTokens = () => {
        const isDark = root.classList.contains("dark");
        root.style.setProperty("--primary-foreground", getContrastForeground(primaryColor));
        root.style.setProperty("--accent", resolveBrandAccent(primaryColor, isDark));
      };
      root.style.setProperty("--org-primary", primaryColor);
      root.style.setProperty("--org-secondary", secondaryColor);
      root.style.setProperty("--org-primary-rgb", orgPrimaryRgb);
      root.style.setProperty("--org-secondary-rgb", orgSecondaryRgb);
      root.style.setProperty("--primary", primaryColor);
      root.style.setProperty("--ring", primaryColor);
      updateThemeTokens();
      const observer = new MutationObserver(mutations => {
        for (const m of mutations) {
          if (m.type === "attributes" && m.attributeName === "class") {
            updateThemeTokens();
          }
        }
      });
      observer.observe(root, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return () => {
        observer.disconnect();
        root.style.removeProperty("--org-primary");
        root.style.removeProperty("--org-secondary");
        root.style.removeProperty("--org-primary-rgb");
        root.style.removeProperty("--org-secondary-rgb");
        root.style.removeProperty("--accent");
        root.style.removeProperty("--primary");
        root.style.removeProperty("--primary-foreground");
        root.style.removeProperty("--ring");
      };
    };
    t19 = [orgPrimary, orgSecondary, orgPrimaryRgb, orgSecondaryRgb];
    $[52] = orgPrimary;
    $[53] = orgPrimaryRgb;
    $[54] = orgSecondary;
    $[55] = orgSecondaryRgb;
    $[56] = t18;
    $[57] = t19;
  } else {
    t18 = $[56];
    t19 = $[57];
  }
  useEffect(t18, t19);
  const t20 = `border-b border-border/20 transition-all duration-300 ${sidebarCollapsed ? "p-2" : "p-3"}`;
  const t21 = `group flex items-center rounded-lg glass-subtle transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 ${sidebarCollapsed ? "justify-center p-1.5" : "p-2"}`;
  let t22;
  if ($[58] !== avatarLogoError || $[59] !== currentOrganization || $[60] !== isGlobalAdmin || $[61] !== logoError || $[62] !== sidebarCollapsed) {
    t22 = sidebarCollapsed ? <div className="overflow-hidden flex items-center justify-center flex-shrink-0 rounded-md h-8 w-8">{currentOrganization?.avatar_logo_url && !avatarLogoError ? <img src={currentOrganization.avatar_logo_url} alt={currentOrganization.name || "Organisation avatar"} className="h-full w-full object-contain" onError={() => setAvatarLogoErrorOrgId(currentOrganization?.id || null)} /> : <div className="h-full w-full rounded-md flex items-center justify-center" style={{
        background: `linear-gradient(135deg, ${currentOrganization?.primary_color || "#6366f1"}, ${currentOrganization?.secondary_color || "#818cf8"})`
      }}><span className="text-white font-semibold text-sm">{currentOrganization?.name?.charAt(0).toUpperCase() || "P"}</span></div>}</div> : currentOrganization?.brand_logo_url && !logoError ? <div className="overflow-hidden flex items-center justify-center flex-shrink-0 rounded-lg w-full border border-border/30 dark:border-transparent p-1"><img src={currentOrganization.brand_logo_url} alt={currentOrganization.name || "Organisation logo"} className="object-contain w-full h-10 drop-shadow-[0_0_3px_rgba(0,0,0,0.5)] dark:drop-shadow-none" onError={() => setLogoErrorOrgId(currentOrganization?.id || null)} /></div> : <div className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0" style={{
        background: currentOrganization?.avatar_logo_url && !avatarLogoError ? undefined : `linear-gradient(135deg, ${currentOrganization?.primary_color || "#6366f1"}, ${currentOrganization?.secondary_color || "#818cf8"})`
      }}>{currentOrganization?.avatar_logo_url && !avatarLogoError ? <img src={currentOrganization.avatar_logo_url} alt={currentOrganization.name || "Organisation avatar"} className="h-full w-full object-cover" onError={() => setAvatarLogoErrorOrgId(currentOrganization?.id || null)} /> : <span className="text-white font-bold text-base">{currentOrganization?.name?.charAt(0).toUpperCase() || "P"}</span>}</div><div className="min-w-0"><p className="text-sm font-semibold truncate">{currentOrganization?.name || "Penny Admin"}</p><p className="text-[11px] text-muted-foreground/70">{isGlobalAdmin ? "Global Admin" : "Org Admin"}</p></div></div>;
    $[58] = avatarLogoError;
    $[59] = currentOrganization;
    $[60] = isGlobalAdmin;
    $[61] = logoError;
    $[62] = sidebarCollapsed;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t21 || $[65] !== t22) {
    t23 = <Link href="/admin" className={t21}>{t22}</Link>;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== organizations.length || $[68] !== sidebarCollapsed) {
    t24 = organizations.length > 1 && !sidebarCollapsed && <button onClick={() => setOrgSelectorOpen(true)} className="mt-2 flex items-center justify-between w-full rounded-md px-2 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-white/[0.08] dark:hover:bg-white/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"><span className="truncate">Switch organisation</span><ChevronDown className="h-3 w-3 flex-shrink-0" /></button>;
    $[67] = organizations.length;
    $[68] = sidebarCollapsed;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== t20 || $[71] !== t23 || $[72] !== t24) {
    t25 = <div className={t20}>{t23}{t24}</div>;
    $[70] = t20;
    $[71] = t23;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  const t26 = activeSubMenu && !sidebarCollapsed ? "translateX(-100%)" : "translateX(0)";
  let t27;
  if ($[74] !== t26) {
    t27 = {
      transform: t26
    };
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  const orgAccent = currentOrganization?.primary_color || "#3b82f6";
  let t28;
  if ($[76] !== currentOrganization?.id || $[77] !== isPlatformAdmin || $[78] !== renderNavItem || $[79] !== sidebarCollapsed) {
    t28 = (items, label_0, accentColor) => {
      const opts = {
        accentColor,
        collapsed: sidebarCollapsed
      };
      return <NavSection key={label_0 ?? "__no-label__"} items={items} label={label_0} options={opts} currentOrgId={currentOrganization?.id} isPlatformAdmin={isPlatformAdmin} renderItem={it => renderNavItem(it, opts)} renderLabel={renderSectionLabel} />;
    };
    $[76] = currentOrganization?.id;
    $[77] = isPlatformAdmin;
    $[78] = renderNavItem;
    $[79] = sidebarCollapsed;
    $[80] = t28;
  } else {
    t28 = $[80];
  }
  const buildSection = t28;
  let t29;
  if ($[81] !== activeSubMenu || $[82] !== currentOrganization?.primary_color || $[83] !== renderSubMenuPage) {
    t29 = activeSubMenu && renderSubMenuPage(activeSubMenu, {
      accentColor: activeSubMenu.section === "platform" ? "#a855f7" : currentOrganization?.primary_color || "#3b82f6"
    });
    $[81] = activeSubMenu;
    $[82] = currentOrganization?.primary_color;
    $[83] = renderSubMenuPage;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== t29) {
    t30 = <div className="py-2 px-2 overflow-y-auto">{t29}</div>;
    $[85] = t29;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  const t31 = `border-t border-border/20 transition-all duration-300 ${sidebarCollapsed ? "p-1.5" : "p-2"}`;
  const t32 = `group flex items-center w-full rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 hover:bg-white/[0.08] dark:hover:bg-white/[0.04] ${sidebarCollapsed ? "justify-center p-2" : "gap-2.5 px-2.5 py-2"}`;
  const t33 = `rounded-full bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0 ${sidebarCollapsed ? "h-7 w-7" : "h-8 w-8"}`;
  let t34;
  if ($[87] !== user?.email || $[88] !== userProfile) {
    t34 = userProfile?.avatar_url ? <img src={userProfile.avatar_url} alt="Avatar" className="h-full w-full object-cover" /> : <span className="text-sm font-medium text-primary">{userProfile?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}</span>;
    $[87] = user?.email;
    $[88] = userProfile;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== t33 || $[91] !== t34) {
    t35 = <div className={t33}>{t34}</div>;
    $[90] = t33;
    $[91] = t34;
    $[92] = t35;
  } else {
    t35 = $[92];
  }
  let t36;
  if ($[93] !== sidebarCollapsed || $[94] !== user?.email || $[95] !== userProfile?.full_name) {
    t36 = !sidebarCollapsed && <><div className="min-w-0 flex-1 text-left"><p className="text-[13px] font-medium truncate text-foreground">{userProfile?.full_name || user?.email}</p><p className="text-[11px] text-muted-foreground/70 truncate">{user?.email}</p></div><ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" /></>;
    $[93] = sidebarCollapsed;
    $[94] = user?.email;
    $[95] = userProfile?.full_name;
    $[96] = t36;
  } else {
    t36 = $[96];
  }
  let t37;
  if ($[97] !== t32 || $[98] !== t35 || $[99] !== t36) {
    t37 = <DropdownMenuTrigger asChild={true}><button className={t32}>{t35}{t36}</button></DropdownMenuTrigger>;
    $[97] = t32;
    $[98] = t35;
    $[99] = t36;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  const t38 = sidebarCollapsed ? "right" : "top";
  const t39 = sidebarCollapsed ? "end" : "start";
  let t40;
  if ($[101] !== user?.email || $[102] !== userProfile) {
    t40 = <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">{userProfile?.avatar_url ? <img src={userProfile.avatar_url} alt="Avatar" className="h-full w-full object-cover" /> : <span className="text-sm font-semibold text-primary">{userProfile?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}</span>}</div>;
    $[101] = user?.email;
    $[102] = userProfile;
    $[103] = t40;
  } else {
    t40 = $[103];
  }
  let t41;
  if ($[104] !== userProfile) {
    t41 = userProfile?.full_name && <p className="text-sm font-medium truncate">{userProfile.full_name}</p>;
    $[104] = userProfile;
    $[105] = t41;
  } else {
    t41 = $[105];
  }
  const t42 = `text-xs ${userProfile?.full_name ? "text-muted-foreground" : "text-sm font-medium"} truncate`;
  const t43 = user?.email;
  let t44;
  if ($[106] !== t42 || $[107] !== t43) {
    t44 = <p className={t42}>{t43}</p>;
    $[106] = t42;
    $[107] = t43;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  const t45 = currentOrganization?.name || "No organisation selected";
  let t46;
  if ($[109] !== t45) {
    t46 = <p className="text-xs text-muted-foreground">{t45}</p>;
    $[109] = t45;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== t41 || $[112] !== t44 || $[113] !== t46) {
    t47 = <div className="min-w-0">{t41}{t44}{t46}</div>;
    $[111] = t41;
    $[112] = t44;
    $[113] = t46;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== t40 || $[116] !== t47) {
    t48 = <div className="flex items-center gap-3">{t40}{t47}</div>;
    $[115] = t40;
    $[116] = t47;
    $[117] = t48;
  } else {
    t48 = $[117];
  }
  let t49;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Role</span>;
    $[118] = t49;
  } else {
    t49 = $[118];
  }
  const t50 = currentOrganization?.role_slug || "No role";
  let t51;
  if ($[119] !== t50) {
    t51 = <span className="text-xs font-medium">{t50}</span>;
    $[119] = t50;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] !== isPlatformAdmin) {
    t52 = isPlatformAdmin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"><Shield className="h-2.5 w-2.5" />Penny Admin</span>;
    $[121] = isPlatformAdmin;
    $[122] = t52;
  } else {
    t52 = $[122];
  }
  let t53;
  if ($[123] !== t51 || $[124] !== t52) {
    t53 = <div className="mt-3 flex items-center gap-2">{t49}{t51}{t52}</div>;
    $[123] = t51;
    $[124] = t52;
    $[125] = t53;
  } else {
    t53 = $[125];
  }
  let t54;
  if ($[126] !== t48 || $[127] !== t53) {
    t54 = <DropdownMenuLabel className="p-2"><div className="glass-subtle rounded-2xl p-3">{t48}{t53}</div></DropdownMenuLabel>;
    $[126] = t48;
    $[127] = t53;
    $[128] = t54;
  } else {
    t54 = $[128];
  }
  let t55;
  if ($[129] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <DropdownMenuSeparator />;
    $[129] = t55;
  } else {
    t55 = $[129];
  }
  let t56;
  let t57;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <DropdownMenuItem asChild={true}><Link href="/admin/settings" className="flex items-center"><Settings className="mr-2 h-4 w-4" />Settings</Link></DropdownMenuItem>;
    t57 = <DropdownMenuSeparator />;
    $[130] = t56;
    $[131] = t57;
  } else {
    t56 = $[130];
    t57 = $[131];
  }
  let t58;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <LogOut className="mr-2 h-4 w-4" />;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  let t59;
  if ($[133] !== handleSignOut) {
    t59 = <DropdownMenuItem onClick={handleSignOut}>{t58}Sign Out</DropdownMenuItem>;
    $[133] = handleSignOut;
    $[134] = t59;
  } else {
    t59 = $[134];
  }
  let t60;
  if ($[135] !== t38 || $[136] !== t39 || $[137] !== t54 || $[138] !== t59) {
    t60 = <DropdownMenuContent side={t38} align={t39} className="w-72 glass-strong border border-white/10 p-2">{t54}{t55}{t56}{t57}{t59}</DropdownMenuContent>;
    $[135] = t38;
    $[136] = t39;
    $[137] = t54;
    $[138] = t59;
    $[139] = t60;
  } else {
    t60 = $[139];
  }
  let t61;
  if ($[140] !== t37 || $[141] !== t60) {
    t61 = <DropdownMenu>{t37}{t60}</DropdownMenu>;
    $[140] = t37;
    $[141] = t60;
    $[142] = t61;
  } else {
    t61 = $[142];
  }
  let t62;
  if ($[143] !== t31 || $[144] !== t61) {
    t62 = <div className={t31}>{t61}</div>;
    $[143] = t31;
    $[144] = t61;
    $[145] = t62;
  } else {
    t62 = $[145];
  }
  const t63 = "flex-1 flex flex-col overflow-hidden";
  const t64 = "glass-subtle border-b px-4 lg:px-6 py-3 flex items-center justify-between";
  const t65 = "flex items-center gap-4";
  let t66;
  if ($[146] !== sidebarOpen) {
    t66 = <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</Button>;
    $[146] = sidebarOpen;
    $[147] = t66;
  } else {
    t66 = $[147];
  }
  let t67;
  if ($[148] !== sidebarCollapsed) {
    t67 = () => setSidebarCollapsed(!sidebarCollapsed);
    $[148] = sidebarCollapsed;
    $[149] = t67;
  } else {
    t67 = $[149];
  }
  const t68 = sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar";
  let t69;
  if ($[150] !== sidebarCollapsed) {
    t69 = sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />;
    $[150] = sidebarCollapsed;
    $[151] = t69;
  } else {
    t69 = $[151];
  }
  let t70;
  if ($[152] !== t67 || $[153] !== t68 || $[154] !== t69) {
    t70 = <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={t67} title={t68}>{t69}</Button>;
    $[152] = t67;
    $[153] = t68;
    $[154] = t69;
    $[155] = t70;
  } else {
    t70 = $[155];
  }
  const t71 = "text-lg font-semibold lg:hidden";
  let t72;
  bb0: {
    for (const item_8 of filteredNavItems) {
      if (item_8.children) {
        const child_1 = item_8.children.find(c => pathname === c.href || c.href !== "/admin" && pathname.startsWith(c.href));
        if (child_1) {
          t72 = child_1.name;
          break bb0;
        }
      }
      if (pathname === item_8.href || item_8.href !== "/admin" && pathname.startsWith(item_8.href)) {
        t72 = item_8.name;
        break bb0;
      }
    }
    t72 = "Dashboard";
  }
  let t73;
  if ($[156] !== t72) {
    t73 = <h2 className={t71}>{t72}</h2>;
    $[156] = t72;
    $[157] = t73;
  } else {
    t73 = $[157];
  }
  let t74;
  if ($[158] !== t66 || $[159] !== t70 || $[160] !== t73) {
    t74 = <div className={t65}>{t66}{t70}{t73}</div>;
    $[158] = t66;
    $[159] = t70;
    $[160] = t73;
    $[161] = t74;
  } else {
    t74 = $[161];
  }
  let t75;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <div className="flex items-center gap-2"><ThemeToggle /></div>;
    $[162] = t75;
  } else {
    t75 = $[162];
  }
  let t76;
  if ($[163] !== t74) {
    t76 = <header className={t64}>{t74}{t75}</header>;
    $[163] = t74;
    $[164] = t76;
  } else {
    t76 = $[164];
  }
  let t77;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <ReleaseVersion className="mt-auto py-2 text-center text-xs" />;
    $[165] = t77;
  } else {
    t77 = $[165];
  }
  let t78;
  if ($[166] !== children) {
    t78 = <main className="flex-1 overflow-y-auto flex flex-col">{children}{t77}</main>;
    $[166] = children;
    $[167] = t78;
  } else {
    t78 = $[167];
  }
  let t79;
  if ($[168] !== t76 || $[169] !== t78) {
    t79 = <div className={t63}>{t76}{t78}</div>;
    $[168] = t76;
    $[169] = t78;
    $[170] = t79;
  } else {
    t79 = $[170];
  }
  let t80;
  if ($[171] !== showMFAWizard) {
    t80 = <MFAEnrollmentWizard isOpen={showMFAWizard} onComplete={handleMFAComplete} />;
    $[171] = showMFAWizard;
    $[172] = t80;
  } else {
    t80 = $[172];
  }
  const t81 = currentOrganization?.id;
  let t82;
  if ($[173] !== handleOrgSwitch || $[174] !== orgSelectorOpen || $[175] !== organizations || $[176] !== t81) {
    t82 = <OrgSelectorModal open={orgSelectorOpen} onOpenChange={setOrgSelectorOpen} organizations={organizations} selectedOrgId={t81} onSelect={handleOrgSwitch} />;
    $[173] = handleOrgSwitch;
    $[174] = orgSelectorOpen;
    $[175] = organizations;
    $[176] = t81;
    $[177] = t82;
  } else {
    t82 = $[177];
  }
  let t83;
  if ($[178] === Symbol.for("react.memo_cache_sentinel")) {
    t83 = <AgentInactivityGuard />;
    $[178] = t83;
  } else {
    t83 = $[178];
  }
  return <div className="admin-shell h-screen flex overflow-hidden" style={cssVariables}><aside className={`hidden lg:flex lg:flex-col admin-nav transition-all duration-300 ${sidebarCollapsed ? "lg:w-14" : "lg:w-56"}`}><div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden">{t25}<nav className={`flex-1 min-h-0 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? "px-1.5" : ""}`}><div className="grid grid-cols-[100%_100%] h-full transition-transform duration-250 ease-out" style={t27}><div className="py-2 px-2 overflow-y-auto"><>{buildSection(navSections.overview, null, orgAccent)}{buildSection(navSections.operations, "Operations", orgAccent)}{buildSection(navSections.build, "Build", orgAccent)}{buildSection(navSections.administration, "Administration", orgAccent)}{buildSection(navSections.platform, "Platform", "#a855f7")}</></div>{t30}</div></nav>{t62}</div></aside>{t79}{sidebarOpen && <div className="lg:hidden fixed inset-0 z-50 flex"><div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} /><aside className="relative w-56 admin-nav flex flex-col"><div className="flex-1 flex flex-col overflow-y-auto"><div className="border-b border-border/20 p-3"><Link href="/admin" onClick={() => setSidebarOpen(false)} className="flex items-center rounded-lg glass-subtle p-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40">{currentOrganization?.brand_logo_url && !logoError ? <div className="overflow-hidden flex items-center justify-center flex-shrink-0 rounded-lg w-full border border-border/30 dark:border-transparent p-1"><img src={currentOrganization.brand_logo_url} alt={currentOrganization.name || "Organisation logo"} className="object-contain w-full h-10 drop-shadow-[0_0_3px_rgba(0,0,0,0.5)] dark:drop-shadow-none" onError={() => setLogoErrorOrgId(currentOrganization?.id || null)} /></div> : <div className="flex items-center gap-2.5"><div className="h-9 w-9 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0" style={{
                  background: currentOrganization?.avatar_logo_url && !avatarLogoError ? undefined : `linear-gradient(135deg, ${currentOrganization?.primary_color || "#6366f1"}, ${currentOrganization?.secondary_color || "#818cf8"})`
                }}>{currentOrganization?.avatar_logo_url && !avatarLogoError ? <img src={currentOrganization.avatar_logo_url} alt={currentOrganization.name || "Organisation avatar"} className="h-full w-full object-cover" onError={() => setAvatarLogoErrorOrgId(currentOrganization?.id || null)} /> : <span className="text-white font-bold text-base">{currentOrganization?.name?.charAt(0).toUpperCase() || "P"}</span>}</div><div className="min-w-0"><p className="text-sm font-semibold truncate">{currentOrganization?.name || "Penny Admin"}</p><p className="text-[11px] text-muted-foreground/70">{isGlobalAdmin ? "Global Admin" : "Org Admin"}</p></div></div>}</Link>{organizations.length > 1 && <button onClick={() => {
              setOrgSelectorOpen(true);
              setSidebarOpen(false);
            }} className="mt-2 flex items-center justify-between w-full rounded-md px-2 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-white/[0.08] dark:hover:bg-white/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"><span className="truncate">Switch organisation</span><ChevronDown className="h-3 w-3 flex-shrink-0" /></button>}</div><nav className="flex-1 min-h-0 overflow-hidden"><div className="grid grid-cols-[100%_100%] h-full transition-transform duration-250 ease-out" style={{
              transform: activeSubMenu ? "translateX(-100%)" : "translateX(0)"
            }}><div className="py-2 px-2 overflow-y-auto">{(() => {
                  const orgAccent_0 = currentOrganization?.primary_color || "#3b82f6";
                  const buildSection_0 = (items_0, label_1, accentColor_0) => {
                    const opts_0 = {
                      accentColor: accentColor_0,
                      onClick: () => setSidebarOpen(false)
                    };
                    return <NavSection key={label_1 ?? "__no-label__"} items={items_0} label={label_1} options={opts_0} currentOrgId={currentOrganization?.id} isPlatformAdmin={isPlatformAdmin} renderItem={it_0 => renderNavItem(it_0, opts_0)} renderLabel={renderSectionLabel} />;
                  };
                  return <>{buildSection_0(navSections.overview, null, orgAccent_0)}{buildSection_0(navSections.operations, "Operations", orgAccent_0)}{buildSection_0(navSections.build, "Build", orgAccent_0)}{buildSection_0(navSections.administration, "Administration", orgAccent_0)}{buildSection_0(navSections.platform, "Platform", "#a855f7")}</>;
                })()}</div><div className="py-2 px-2 overflow-y-auto">{activeSubMenu && renderSubMenuPage(activeSubMenu, {
                  accentColor: activeSubMenu.section === "platform" ? "#a855f7" : currentOrganization?.primary_color || "#3b82f6",
                  onClick: () => setSidebarOpen(false)
                })}</div></div></nav><div className="border-t border-border/20 p-2"><DropdownMenu><DropdownMenuTrigger asChild={true}><button className="group flex items-center gap-2.5 w-full rounded-lg px-2.5 py-2 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 hover:bg-white/[0.08] dark:hover:bg-white/[0.04]"><div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden flex-shrink-0">{userProfile?.avatar_url ? <img src={userProfile.avatar_url} alt="Avatar" className="h-full w-full object-cover" /> : <span className="text-sm font-medium text-primary">{userProfile?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}</span>}</div><div className="min-w-0 flex-1 text-left"><p className="text-[13px] font-medium truncate text-foreground">{userProfile?.full_name || user?.email}</p><p className="text-[11px] text-muted-foreground/70 truncate">{user?.email}</p></div><ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 flex-shrink-0" /></button></DropdownMenuTrigger><DropdownMenuContent side="top" align="start" className="w-64 glass-strong border border-white/10 p-2"><DropdownMenuLabel className="p-2"><p className="text-xs text-muted-foreground truncate">{user?.email}</p><div className="mt-1 flex items-center gap-2"><span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Role</span><span className="text-xs font-medium">{currentOrganization?.role_slug || "No role"}</span>{isPlatformAdmin && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300"><Shield className="h-2.5 w-2.5" />Penny Admin</span>}</div></DropdownMenuLabel><DropdownMenuSeparator /><DropdownMenuItem asChild={true}><Link href="/admin/settings" className="flex items-center" onClick={() => setSidebarOpen(false)}><Settings className="mr-2 h-4 w-4" />Settings</Link></DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem onClick={handleSignOut}><LogOut className="mr-2 h-4 w-4" />Sign Out</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div></div></aside></div>}{t80}{t82}{t83}</div>;
}

// Public admin routes that must render without the ProtectedRoute guard.
// Must stay aligned with ADMIN_EXCLUDED in middleware.ts.
function _temp9(hexColor) {
  const normalized = hexColor.trim().replace("#", "");
  const isShort = normalized.length === 3;
  const isValid = isShort || normalized.length === 6;
  if (!isValid || !/^[0-9a-fA-F]+$/.test(normalized)) {
    return null;
  }
  const full = isShort ? normalized.split("").map(_temp8).join("") : normalized;
  const red = parseInt(full.slice(0, 2), 16);
  const green = parseInt(full.slice(2, 4), 16);
  const blue = parseInt(full.slice(4, 6), 16);
  return `${red} ${green} ${blue}`;
}
function _temp8(char) {
  return `${char}${char}`;
}
function _temp7(item_7) {
  return item_7.section === "platform";
}
function _temp6(item_6) {
  return item_6.section === "administration";
}
function _temp5(item_5) {
  return item_5.section === "build";
}
function _temp4(item_4) {
  return item_4.section === "operations";
}
function _temp3(item_3) {
  return item_3.section === "overview";
}
function _temp2(label) {
  return <div className="px-2 pt-4 pb-1"><p className="text-[11px] font-medium text-muted-foreground/70 uppercase tracking-wider">{label}</p></div>;
}
const PUBLIC_ADMIN_PATHS = ['/admin/login', '/admin/forgot-password', '/admin/reset-password'];
export default function AdminLayout(t0) {
  const $ = _c(6);
  const {
    children
  } = t0;
  const pathname = usePathname();
  if (PUBLIC_ADMIN_PATHS.some(p => pathname?.startsWith(p))) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (isBrandingPreviewPath(pathname)) {
    let t1;
    if ($[2] !== children) {
      t1 = <ProtectedRoute adminOnly={true} loginRedirect="admin">{children}</ProtectedRoute>;
      $[2] = children;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== children) {
    t1 = <ProtectedRoute adminOnly={true} loginRedirect="admin"><AdminLayoutContent>{children}</AdminLayoutContent></ProtectedRoute>;
    $[4] = children;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}